import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const WaterUsageEfficiency = () => {
  // State variables
  const [cropYield, setCropYield] = useState(0); // Crop yield value
  const [cropYieldUnit, setCropYieldUnit] = useState("kg"); // Unit for crop yield
  const [totalWaterUsed, setTotalWaterUsed] = useState(0); // Total water used value
  const [waterUsedUnit, setWaterUsedUnit] = useState("liters"); // Unit for total water used
  const [waterUsageEfficiency, setWaterUsageEfficiency] = useState(0); // Calculated water usage efficiency

  // Conversion factors for units
  const unitConversion = {
    // Crop Yield Units
    kg: 1,
    ton: 1000,
    lb: 0.453592,
    quintal: 100,
    gram: 0.001,

    // Water Usage Units
    liters: 1,
    cubicMeters: 1000,
    gallons: 3.78541,
    acreFeet: 1233.48,
    milliliters: 0.001,
  };

  // Event handlers for input changes
  const handleCropYieldChange = (e) =>
    setCropYield(parseFloat(e.target.value) || 0);
  const handleCropYieldUnitChange = (e) => setCropYieldUnit(e.target.value);
  const handleTotalWaterUsedChange = (e) =>
    setTotalWaterUsed(parseFloat(e.target.value) || 0);
  const handleWaterUsedUnitChange = (e) => setWaterUsedUnit(e.target.value);

  // Calculate Water Use Efficiency when inputs or units change
  useEffect(() => {
    if (totalWaterUsed > 0) {
      const normalizedYield = cropYield * unitConversion[cropYieldUnit];
      const normalizedWaterUsed = totalWaterUsed * unitConversion[waterUsedUnit];
      const efficiency = normalizedYield / normalizedWaterUsed;
      setWaterUsageEfficiency(efficiency);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [cropYield, cropYieldUnit, totalWaterUsed, waterUsedUnit]);

  return (
    <Layout title="Water Usage Efficiency Calculator" path="water-usage-efficiency">
      <section className="section-padding" id="water-usage-efficiency">
        <div className="container mt-5">
          <div className="row">
            {/* Calculator Section */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Water Usage Efficiency Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Crop Yield */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="cropYield" className="form-label">
                        Crop Yield
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Crop Yield:</strong> The total amount of crops harvested from a certain area. This is the output from the crops grown during a specific season."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="cropYield"
                        placeholder="Enter crop yield"
                        value={cropYield || ""}
                        onChange={handleCropYieldChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={cropYieldUnit}
                        onChange={handleCropYieldUnitChange}
                      >
                        <option value="kg">Kilograms (kg)</option>
                        <option value="ton">Tons</option>
                        <option value="lb">Pounds (lb)</option>
                        <option value="quintal">Quintals</option>
                        <option value="gram">Grams</option>
                      </select>
                    </div>

                    {/* Input for Total Water Used */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalWaterUsed" className="form-label">
                        Total Water Used
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Water Used:</strong> The total amount of water consumed for growing crops during a specific time period, measured in various units."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalWaterUsed"
                        placeholder="Enter total water used"
                        value={totalWaterUsed || ""}
                        onChange={handleTotalWaterUsedChange}
                      />
                      <select
                        className="form-select mt-2"
                        value={waterUsedUnit}
                        onChange={handleWaterUsedUnitChange}
                      >
                        <option value="liters">Liters</option>
                        <option value="cubicMeters">Cubic Meters</option>
                        <option value="gallons">Gallons</option>
                        <option value="acreFeet">Acre-Feet</option>
                        <option value="milliliters">Milliliters</option>
                      </select>
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Water Use Efficiency:{" "}
                        <span className="fw-bold ms-3">
                          {waterUsageEfficiency === 0
                            ? "0"
                            : waterUsageEfficiency.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          (yield per water unit)
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Water Usage Efficiency Calculation
                </h3>

                {/* What is Water Use Efficiency? */}
                <h4 className="mt-3 mb-3">What is Water Use Efficiency?</h4>
                <p>
                  **Water Use Efficiency (WUE)** measures how effectively water is used to produce crops. It is calculated as the ratio of crop yield to the total water consumed during cultivation.
                </p>

                {/* Why Use the Water Usage Efficiency Calculator? */}
                <h4 className="mt-4 mb-3">Why Use the Water Usage Efficiency Calculator?</h4>
                <p>
                  This calculator helps farmers and agribusinesses optimize water usage, minimize waste, and plan irrigation strategies more effectively.
                </p>

                {/* Benefits of Using the Water Usage Efficiency Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using the Water Usage Efficiency Calculator</h4>
                <ul>
                  <li>
                    <strong>Supports Water Conservation:</strong> Encourages efficient use of water resources.
                  </li>
                  <li>
                    <strong>Improves Crop Planning:</strong> Helps in selecting crops based on water availability and efficiency.
                  </li>
                  <li>
                    <strong>Optimizes Resource Allocation:</strong> Balances water usage with expected crop output.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Water Usage Efficiency Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li>
                    <strong>Irrigated Farming:</strong> Enhances irrigation efficiency and water management.
                  </li>
                  <li>
                    <strong>Agribusiness:</strong> Helps agribusinesses achieve better returns with optimized water usage.
                  </li>
                  <li>
                    <strong>Sustainable Agriculture:</strong> Supports environmentally-friendly farming practices.
                  </li>
                </ul>

                {/* Question 1 */}
                <h5 className="mt-3 mb-2">
                  How can Water Usage Efficiency improve sustainability?
                </h5>
                <p>
                  Efficient water usage reduces strain on water resources, promotes sustainable farming, and ensures long-term agricultural productivity.
                </p>

                {/* Question 2 */}
                <h5 className="mt-3 mb-2">
                  Can this calculator be used for different crop types?
                </h5>
                <p>
                  Yes, the calculator is versatile and can be used for various crops, provided crop yield and water usage data are available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WaterUsageEfficiency;
